import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ArrowLeft, Heart } from "lucide-react";

// Very light markdown-ish renderer: ## for h2, > blockquote, - lists, **bold**
function renderBody(body = "") {
  const blocks = body.split(/\n{2,}/);
  return blocks.map((b, i) => {
    if (b.startsWith("## ")) return <h2 key={i} className="font-display text-3xl mt-10 mb-4">{b.slice(3)}</h2>;
    if (b.startsWith("> ")) return <blockquote key={i} className="border-l-4 border-brand-pink pl-5 italic text-neutral-700 my-6">{b.slice(2)}</blockquote>;
    if (/^\d+\.\s/.test(b) || b.startsWith("- ")) {
      const items = b.split("\n").map((li) => li.replace(/^(\d+\.\s|\-\s)/, ""));
      const Tag = /^\d+\./.test(b) ? "ol" : "ul";
      return <Tag key={i} className={`${Tag === "ol" ? "list-decimal" : "list-disc"} pl-6 space-y-2 my-5 text-neutral-700`}>{items.map((li, j) => <li key={j}>{li}</li>)}</Tag>;
    }
    // bold **x**
    const parts = b.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : p);
    return <p key={i} className="text-neutral-800 leading-relaxed my-4">{parts}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/blog/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setErr("Post not found"));
  }, [slug]);

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-brand-pink mb-8" data-testid="blog-back">
          <ArrowLeft className="w-4 h-4" /> All posts
        </Link>
        {err && <div className="text-center py-24 text-neutral-500">{err}</div>}
        {post && (
          <article data-testid="blog-post-article">
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3 inline-flex items-center gap-2">
              <Heart className="w-3 h-3" fill="#E91E63" /> {post.category}
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight mb-4">{post.title}</h1>
            <div className="text-neutral-500 text-sm mb-8">By {post.author} · {new Date(post.created_at).toLocaleDateString()}</div>
            {post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="w-full rounded-3xl aspect-[16/9] object-cover mb-8" />
            )}
            <div className="text-lg text-neutral-800 leading-relaxed">{renderBody(post.body)}</div>
          </article>
        )}
      </div>
      <Footer />
    </div>
  );
}
